import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { ShopperAgentGenerationMode } from "@redotech/crdb-enums/shopper-ai/shopper-agent-generation-mode";
import { ShopperAgentAutomationKind } from "@redotech/redo-shopper-agent-common/automation-kind";
import { ShopperAgentOutreachOrigin } from "@redotech/redo-shopper-agent-common/outreach-origin";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import {
  DatabaseError,
  DataIntegrityError,
  OpenSearchError,
} from "../auth/errors";
import { TopicId } from "../ids/definitions";
import {
  ShopperAgentConversationAnalysisSentiment,
  ShopperAgentConversationDetailResponse,
  ShopperAgentConversationFilterTab,
  ShopperAgentConversationListResponse,
} from "../models/shopper-ai/conversation/shopper-agent";
import {
  ShopperAgentAggregateInsightDetailResponse,
  ShopperAgentAggregateInsightRunListResponse,
  ShopperAgentSpotCheckResponse,
} from "../models/shopper-ai/conversation/shopper-agent-aggregate-insight";
import { enumLiterals } from "../schema/helpers";
import { DEFAULT_LIMIT, MAX_LIMIT } from "../schema/pagination";
import {
  StartShopperAgentExportInput,
  StartShopperAgentExportResponse,
} from "./shopper-agent-export";

export const ShopperAgentConversationListQueryParams = Schema.Struct({
  limit: Schema.optionalWith(
    Schema.NumberFromString.pipe(
      Schema.int(),
      Schema.positive(),
      Schema.lessThanOrEqualTo(MAX_LIMIT),
    ),
    { default: () => DEFAULT_LIMIT },
  ),
  offset: Schema.optionalWith(
    Schema.NumberFromString.pipe(Schema.int(), Schema.nonNegative()),
    { default: () => 0 },
  ),
  filterTab: Schema.optionalWith(ShopperAgentConversationFilterTab, {
    default: () => "all" as const,
  }),
  /** Playbook trigger key; only conversations with that outreach appear. */
  outreachTopic: Schema.optional(Schema.String),
  outreachOrigin: Schema.optional(
    Schema.Literal(...enumLiterals(ShopperAgentOutreachOrigin)),
  ),
  /** How the outreach that opened the conversation was written. */
  outreachGenerationMode: Schema.optional(
    Schema.Literal(...enumLiterals(ShopperAgentGenerationMode)),
  ),
  outreachAutomationKind: Schema.optional(
    Schema.Literal(...enumLiterals(ShopperAgentAutomationKind)),
  ),
  outreachCampaignId: Schema.optional(Schema.String),
  outreachAutomationId: Schema.optional(Schema.String),
  sentiment: Schema.optional(ShopperAgentConversationAnalysisSentiment),
  lastResponseAtStart: Schema.optional(Schema.DateFromString),
  lastResponseAtEnd: Schema.optional(Schema.DateFromString),
  productFamilyIds: Schema.optional(Schema.Array(Schema.String)),
  /** A URL carries no empty list, so an empty one reads as no filter. */
  topicIds: Schema.optional(Schema.Array(TopicId)),
  /**
   * Case-insensitive keyword match across customer name/email/phone and
   * conversation message text. Applied in OpenSearch, not the current page.
   */
  search: Schema.optional(Schema.String.pipe(Schema.maxLength(200))),
  sortBy: Schema.optionalWith(
    Schema.Literal(
      "lastResponseAt",
      "createdAt",
      "lastCustomerMessageAt",
      "lastMerchantMessageAt",
    ),
    { default: () => "lastResponseAt" as const },
  ),
  sortDirection: Schema.optionalWith(Schema.Literal("asc", "desc"), {
    default: () => "desc" as const,
  }),
});
export type ShopperAgentConversationListQueryParams =
  typeof ShopperAgentConversationListQueryParams.Type;

export const ShopperAgentConversationDetailQueryParams = Schema.Struct({
  /** When set, detail may include scrollToMessageId for the latest matching outreach. */
  outreachTopic: Schema.optional(Schema.String),
  outreachOrigin: Schema.optional(
    Schema.Literal(...enumLiterals(ShopperAgentOutreachOrigin)),
  ),
});
export type ShopperAgentConversationDetailQueryParams =
  typeof ShopperAgentConversationDetailQueryParams.Type;

export const TextConciergeGroup = HttpApiGroup.make("textConcierge")
  .add(
    endpoint.merchant
      .get("listTextConciergeConversations", "/text-concierge/conversations")
      .setUrlParams(ShopperAgentConversationListQueryParams)
      .addSuccess(ShopperAgentConversationListResponse)
      .addError(HttpApiError.Forbidden)
      .addError(OpenSearchError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "listShopperAgentAggregateInsightRuns",
        "/text-concierge/shopper-agent-aggregate-insight-runs",
      )
      .addSuccess(ShopperAgentAggregateInsightRunListResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "listShopperAgentSpotCheckConversations",
        "/text-concierge/shopper-agent-spot-check",
      )
      .addSuccess(ShopperAgentSpotCheckResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getTextConciergeConversationDetail",
        "/text-concierge/conversations/:topicId",
      )
      .setPath(Schema.Struct({ topicId: TopicId }))
      .setUrlParams(ShopperAgentConversationDetailQueryParams)
      .addSuccess(ShopperAgentConversationDetailResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get(
        "getShopperAgentAggregateInsight",
        "/text-concierge/shopper-agent-aggregate-insights/:topicId",
      )
      .setPath(Schema.Struct({ topicId: TopicId }))
      .addSuccess(ShopperAgentAggregateInsightDetailResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("startShopperAgentExport", "/shopper-agent/conversations/export")
      .setPayload(StartShopperAgentExportInput)
      .addSuccess(StartShopperAgentExportResponse)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
